import { alpha } from '@mui/material/styles'
import { blueGrey, grey, red } from '@mui/material/colors'

/**
 * Centralized color tokens for the application
 * Organized by theme mode (dark/light) and purpose
 */

// ============================================================================
// DARK THEME COLORS
// ============================================================================

const DARK_PAPER = 'rgb(22, 28, 42)' // darkest
const DARK_SECTION = 'rgb(30, 39, 53)' // mid
const DARK_DEFAULT = 'rgb(49, 57, 70)' // lightest baseline

export const darkColors = {
  // Primary & Secondary
  primary: {
    lighter: blueGrey[100],
    main: blueGrey[700],
    contrastText: grey[50],
  },
  secondary: {
    main: grey[900],
    contrastText: grey[200],
  },

  // Background Colors
  background: {
    image: 'radial-gradient(circle, rgba(255,255,255,0.08) 1px, transparent 1px)',
    divider: 'rgb(72, 95, 129)',
    pinnedrows: 'rgba(139, 165, 196, 0.28)',
    default: DARK_DEFAULT,
    defaultdim: 'rgba(26, 22, 22, 0.5)',
    section: 'rgba(26, 22, 22, 0.5)',
    subtasksection: 'rgba(30, 39, 53, 0.8)',
    mentions: DARK_SECTION,
    header: 'rgb(26, 22, 22)',
    main: DARK_SECTION,
    tooltip: 'rgba(0, 0, 0, 0.87)',
    input: 'rgb(38, 47, 67)',
    icon: 'rgb(44, 51, 61)',
    sectionContent: 'rgb(60, 72, 85)',
    selected: 'rgb(26, 22, 22)',
    timelineGrid: 'rgba(43, 67, 123, 0.5)',
    help: 'rgb(43, 67, 123)',
    paper: DARK_PAPER,
    disabled: 'rgba(22, 28, 42, 0.3)',
    toolbar: 'rgb(40, 46, 59)',
    loading: 'rgb(107, 185, 240)',
    boxShadow: '0 1px 3px rgba(255, 255, 255, 0.1)',
    isDragging: '0 4px 6px rgba(255, 255, 255, 0.1)',
    overlay: 'rgba(26, 22, 22, 0.1)',
    thumbnail: 'rgba(202, 65, 92, 0.8)',
    details: 'rgb(41, 54, 69)',
    fielddisabled: 'rgb(47, 53, 65)',
    dialogtext: '#2E3B4E',
    snackbar: '',
    // Gradients
    gradientDefault: `linear-gradient(180deg, ${DARK_PAPER} 0%, ${DARK_SECTION} 38%, ${DARK_DEFAULT} 100%)`,
    gradientSection: `linear-gradient(180deg, ${DARK_SECTION} 0%, ${DARK_DEFAULT} 100%)`,
    gradientPaper: `linear-gradient(180deg, ${DARK_PAPER} 0%, ${DARK_SECTION} 100%)`,
    vignette: `radial-gradient(1200px 800px at 50% 0%, ${alpha('#000', 0.0)} 40%, ${alpha('#000', 0.25)} 100%)`,
  },

  // Text Colors
  text: {
    primary: 'rgba(255,255,255,1)',
    contrast: 'rgba(0,0,0,1)',
    shadow: 'rgba(0,0,0,0.5)',
    highlight: 'rgb(107, 185, 240)',
    subtask: 'rgb(50, 158, 235)',
    secondary: 'rgba(255,255,255,0.5)',
    disabled: 'rgba(128, 124, 124, 0.8)',
    tertiary: 'rgba(255,255,255,0.3)',
    resetFilter: 'rgb(255, 165, 93)',
    chip: 'rgba(0,0,0,1)',
    admin: 'rgba(255, 123, 187, 1)',
    success: 'rgb(80, 200, 120)',
    successdim: 'rgba(129, 230, 163, 1)',
    warning: 'rgb(249, 167, 49)',
    dirty: 'rgba(72, 113, 247, 0.3)',
    error: 'rgba(220, 20, 60, 1)',
    errordim: 'rgba(226, 70, 70, 0.5)',
    link: 'rgb(173, 216, 230)',
    revise: 'rgb(255, 131, 88)',
    review: 'rgb(33, 201, 223)',
    htmlText: 'rgba(255,255,255,1)',
    parent: 'rgba(67, 85, 241, 1)',
    size: 12,
  },

  // Icon Colors (shared across themes)
  icon: {
    complete: 'rgb(5, 190, 113)',
    warning: 'rgb(249, 167, 49)',
    decline: 'rgba(220, 20, 60, 0.8)',
    deals: '#fbc11e',
    organizations: 'rgb(114, 229, 218)',
    approvals: 'rgba(205, 254, 194, 1)',
    contacts: 'rgb(255, 123, 187)',
    nco: 'rgb(52, 211, 145)',
    accounting: 'rgb(107, 185, 240)',
    teamtasks: 'rgb(247, 63, 236)',
    website: 'rgb(84, 126, 240)',
    creative: 'rgb(150, 88, 238)',
    seo: 'rgb(238, 111, 141)',
    mtb: 'rgb(20, 119, 248)',
    campaignChecklist: 'rgb(255, 131, 88)',
    projects: 'rgb(245, 108, 53)',
    insight: 'rgb(65, 190, 169)',
    observation: 'rgb(168, 202, 255)',
    leasingPercentage: 'rgba(230, 181, 21, 1)',
    pandadoc: 'rgb(36, 133, 103)',
  },

  // Misc
  badge: {
    dot: 'rgb(255, 165, 93)',
  },
  chip: {
    sizeSmall: { height: 15, width: 15 },
  },
  card: {
    default: 'rgb(18, 26, 41)',
  },
  list: {
    selected: 'rgba(130, 178, 240, 1)',
    focused: 'rgba(66, 85, 110, 1)',
  },
  tertiary: {
    lighter: grey[50],
    light: grey[100],
    main: grey[400],
    dark: grey[700],
    darker: grey[900],
  },
  action: {
    selected: alpha('#90caf9', 0.16),
    selectedOpacity: 0.16,
    hoverOpacity: 0.08,
  },
}

// ============================================================================
// LIGHT THEME COLORS
// ============================================================================

const LIGHT_GRADIENT = 'linear-gradient(135deg, #ffdbdb 0%, #b1eaff 100%)'
const LIGHT_GRID_GRADIENT = 'linear-gradient(135deg, #ffdbdb 0%, #b1eaff 40%)'
const LIGHT_DOTS = 'radial-gradient(circle, rgba(0,0,0,0.2) 1px, transparent 1px)'
const LIGHT_FALLBACK = 'rgba(255,255,255,1)'

export const lightColors = {
  // Primary & Secondary
  primary: {
    lighter: blueGrey[100],
    light: blueGrey[300],
    main: 'rgba(114, 112, 114, 0.7)',
    dark: blueGrey[700],
    darker: blueGrey[900],
    darkest: 'black',
  },
  secondary: {
    light: '#293742',
    main: grey[50],
    dark: grey[50],
    darker: red[900],
  },

  // Background Colors
  background: {
    image: LIGHT_DOTS,
    divider: 'rgb(180, 213, 229)',
    default: LIGHT_FALLBACK,
    defaultdim: 'rgba(236, 239, 248, 0.5)',
    section: 'rgb(255, 255, 255)',
    header: 'rgb(232, 240, 254)',
    main: 'rgba(255,255,255,1)',
    paper: 'rgba(255,255,255,1)',
    disabled: 'rgba(255,255,255,0.3)',
    mentions: 'rgba(179, 178, 178, 0.2)',
    tableRow: 'rgba(255,255,255,1)',
    input: 'rgb(245, 245, 245)',
    sectionContent: 'rgb(215, 228, 234)',
    selected: 'rgb(232, 240, 254)',
    timelineGrid: 'rgb(236, 239, 248)',
    help: 'rgb(236, 239, 248)',
    tooltip: 'rgba(255, 255, 255, 0.87)',
    reviewContent: 'rgba(161, 215, 235, 1)',
    loading: 'rgb(107, 185, 240)',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
    isDragging: '0 4px 6px rgba(0, 0, 0, 0.2)',
    overlay: 'rgba(255,255,255, 0)',
    thumbnail: 'rgba(202, 65, 92, 0.8)',
    details: 'rgb(201, 216, 223)',
    fielddisabled: 'rgb(47, 53, 65)',
    dialogtext: 'rgb(236, 239, 248)',
    toolbar: 'rgba(206, 220, 250, 1)',
    toolbarlight: 'rgba(206, 220, 250, 0.2)',
    gradient: LIGHT_GRADIENT,
    gridgradient: LIGHT_GRID_GRADIENT,
    snackbar: '',
  },

  // Text Colors
  text: {
    primary: 'rgba(0,0,0,1)',
    highlight: 'rgb(107, 185, 240)',
    shadow: 'rgba(255,255,255,0.5)',
    subtask: 'rgb(21, 112, 231)',
    secondary: 'rgba(0,0,0,0.5)',
    disabled: 'rgba(0,0,0,0.2)',
    tertiary: 'rgba(0,0,0,0.3)',
    resetFilter: 'rgb(255, 165, 93)',
    chip: 'rgba(0,0,0,1)',
    admin: 'rgba(255, 123, 187, 1)',
    success: 'rgb(80, 200, 120)',
    successdim: 'rgba(129, 230, 163, 1)',
    warning: 'rgb(249, 167, 49)',
    error: 'rgba(220, 20, 60)',
    errordim: 'rgb(223, 144, 144)',
    link: 'rgb(46, 55, 174)',
    revise: 'rgb(255, 131, 88)',
    review: 'rgb(33, 201, 223)',
    htmlText: '#333333',
    parent: 'rgba(67, 85, 241, 1)',
    size: 12,
  },

  // Icon Colors (same as dark theme)
  icon: darkColors.icon,

  // Misc
  badge: {
    dot: 'rgb(255, 165, 93)',
  },
  chip: {
    sizeSmall: { height: 15, width: 15 },
  },
  card: {
    default: 'rgb(236, 239, 248)',
  },
  tertiary: {
    lighter: grey[50],
    light: grey[100],
    main: grey[400],
    dark: grey[700],
    darker: grey[900],
  },
}

// ============================================================================
// SHARED COLOR UTILITIES
// ============================================================================

/**
 * Status colors used across the application
 */
export const statusColors = {
  success: 'rgb(80, 200, 120)',
  successDim: 'rgba(129, 230, 163, 1)',
  warning: 'rgb(249, 167, 49)',
  error: 'rgba(220, 20, 60, 1)',
  errorDim: 'rgba(226, 70, 70, 0.5)',
  info: 'rgb(107, 185, 240)',
  complete: 'rgb(5, 190, 113)',
  pending: 'rgb(255, 165, 93)',
  declined: 'rgba(220, 20, 60, 0.8)',
}

/**
 * Module/Feature colors for navigation icons
 */
export const moduleColors = {
  deals: '#fbc11e',
  organizations: 'rgb(114, 229, 218)',
  approvals: 'rgba(205, 254, 194, 1)',
  contacts: 'rgb(255, 123, 187)',
  nco: 'rgb(52, 211, 145)',
  accounting: 'rgb(107, 185, 240)',
  teamtasks: 'rgb(247, 63, 236)',
  website: 'rgb(84, 126, 240)',
  creative: 'rgb(150, 88, 238)',
  seo: 'rgb(238, 111, 141)',
  mtb: 'rgb(20, 119, 248)',
  campaignChecklist: 'rgb(255, 131, 88)',
  projects: 'rgb(245, 108, 53)',
  insight: 'rgb(65, 190, 169)',
  observation: 'rgb(168, 202, 255)',
  leasingPercentage: 'rgba(230, 181, 21, 1)',
  pandadoc: 'rgb(36, 133, 103)',
}
